import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of, throwError, switchMap, TimeoutError } from 'rxjs';
import { tap, catchError, map, finalize, timeout } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';

/** Réponse brute du login CAC (FastAPI OAuth2) */
export interface CacLoginResponse {
  access_token: string;
  token_type: string;
}

/** Profil utilisateur tel que renvoyé par GET /api/user/me/profile (CAC) */
export interface CacUserProfile {
  id: number;
  username: string;
  email: string;
  telephone?: string;
  telephone_fixe?: string;
  photo_url?: string;
  role: 'ADMIN' | 'USER';
  is_active: boolean;
  hubspot_id?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Interface unifiée utilisée dans toute l'application */
export interface UserInfo {
  id: string;
  username: string;
  nom?: string;
  prenom?: string;
  email: string;
  role: 'ADMIN' | 'USER' | 'COMMERCIAL' | 'VIEWER';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  telephone?: string;
  telephone_fixe?: string;
  photo_url?: string;
  hubspot_id?: string;
  is_hubspot_user?: boolean;
  is_active?: boolean;
  created_at?: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: UserInfo;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  role?: 'ADMIN' | 'USER';
  telephone?: string;
}

export interface HubSpotAuthResponse {
  authorization_url: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /** URL du backend CAC (FastAPI) pour l'auth et les profils utilisateurs */
  private readonly cacApiUrl = environment.apiUrl;
  private readonly tokenKey = 'access_token';
  private readonly userKey = 'current_user';
  private readonly sessionExpiryKey = 'session_expiry';
  private readonly lastActivityKey = 'last_activity';
  private readonly SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 heures
  private sessionTimer: any = null;

  private currentUserSubject = new BehaviorSubject<UserInfo | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    this.initializeAuthState();
    this.setupActivityTracking();
  }

  /** Convertit un CacUserProfile en UserInfo unifié */
  private mapCacProfile(profile: CacUserProfile): UserInfo {
    return {
      id: String(profile.id),
      username: profile.username,
      nom: profile.username,
      prenom: '',
      email: profile.email,
      role: profile.role,
      status: profile.is_active ? 'ACTIVE' : 'INACTIVE',
      telephone: profile.telephone ?? undefined,
      telephone_fixe: profile.telephone_fixe ?? undefined,
      photo_url: profile.photo_url ?? undefined,
      hubspot_id: profile.hubspot_id ?? undefined,
      is_active: profile.is_active,
      created_at: profile.createdAt ?? undefined,
    };
  }

  /** Récupère le profil du user connecté depuis le CAC */
  private fetchCacProfile(): Observable<UserInfo> {
    const headers = this.getAuthHeaders();
    return this.http
      .get<CacUserProfile>(`${this.cacApiUrl}/user/me/profile`, { headers })
      .pipe(
        map(profile => this.mapCacProfile(profile)),
        tap(user => {
          this.currentUserSubject.next(user);
          this.storeUser(user);
        })
      );
  }

  /**
   * Initialize authentication state on service creation
   */
  private initializeAuthState(): void {
    const token = this.getStoredToken();
    const storedUser = this.getStoredUser();

    console.log('🔍 Initialisation auth state:', { hasToken: !!token, hasUser: !!storedUser });

    if (token) {
      if (storedUser) {
        // Cas normal : token + user présents
        if (this.isSessionExpired()) {
          console.log('❌ Session expirée, déconnexion automatique');
          this.clearAuthState();
          return;
        }

        console.log('✅ Session valide, restauration de l\'état utilisateur');
        this.currentUserSubject.next(storedUser);
        this.syncSharedUserStorage(storedUser);
        this.isAuthenticatedSubject.next(true);
        this.startSessionTimer();
        this.updateLastActivity();
      } else {
        // CAS CRITIQUE : Token présent mais pas de user stocké
        // On tente de récupérer le profil automatiquement
        console.log('⚠️ Token présent mais profil manquant, tentative de récupération...');
        this.fetchCacProfile().subscribe({
          next: (user) => {
            console.log('✅ Profil récupéré avec succès au démarrage');
            this.isAuthenticatedSubject.next(true);
            this.initializeSession(); // Recréer les timestamps de session
          },
          error: (err) => {
            console.error('❌ Impossible de récupérer le profil, déconnexion:', err);
            this.clearAuthState();
          }
        });
      }
    } else {
      console.log('❌ Pas de token stocké');
      this.clearAuthState();
    }
  }


  /**
   * Login via le backend CAC (FastAPI) — envoie les credentials en form-encoded
   * puis charge le profil complet de l'utilisateur.
   */
  login(loginRequest: LoginRequest): Observable<AuthResponse> {
    this.isLoadingSubject.next(true);

    // Le CAC attend application/x-www-form-urlencoded (OAuth2PasswordRequestForm)
    const body = new HttpParams()
      .set('username', loginRequest.email)
      .set('password', loginRequest.password);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http
      .post<CacLoginResponse>(`${this.cacApiUrl}/auth/login`, body.toString(), { headers })
      .pipe(
        timeout(25000),
        tap(response => {
          if (response.access_token) {
            localStorage.setItem(this.tokenKey, response.access_token);
            this.isAuthenticatedSubject.next(true);
          }
        }),
        switchMap(response =>
          this.fetchCacProfile().pipe(
            timeout(60000),
            map(user => ({
              access_token: response.access_token,
              token_type: response.token_type,
              user
            } as AuthResponse)),
            catchError((err: unknown) => {
              if (err instanceof TimeoutError) {
                console.error('❌ Timeout GET /api/user/me/profile');
                return throwError(
                  () =>
                    new HttpErrorResponse({
                      status: 0,
                      statusText: 'Timeout',
                      error: {
                        detail:
                          'Connexion OK, mais le chargement du profil a dépassé 60 s (GET /api/user/me/profile). Souvent : base SQL lente ou verrouillage. Vérifiez les logs uvicorn et la connexion à la base.'
                      }
                    })
                );
              }
              return throwError(() => err);
            })
          )
        ),
        tap(() => {
          this.initializeSession();
          // La navigation post-login est laissée au composant (ex. /select-module puis CAC ou CAS).
        }),
        catchError((err: unknown) => {
          if (err instanceof TimeoutError) {
            console.error('❌ Timeout POST /api/auth/login');
            return throwError(
              () =>
                new HttpErrorResponse({
                  status: 0,
                  statusText: 'Timeout',
                  error: {
                    detail:
                      'Pas de réponse du serveur d’authentification (POST /api/auth/login). Lancez le backend CAC : uvicorn app.main:app --port 8000'
                  }
                })
            );
          }
          return this.handleAuthError(err as HttpErrorResponse);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  /**
   * Déconnexion via le backend CAC
   */
  logout(): Observable<any> {
    this.isLoadingSubject.next(true);
    const headers = this.getAuthHeaders();

    return this.http.post(`${this.cacApiUrl}/auth/logout`, {}, { headers })
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.clearAuthState();
          this.router.navigate(['/login']);
          this.isLoadingSubject.next(false);
        })
      );
  }

  /**
   * Rafraîchit le token via le backend CAC
   */
  refreshToken(): Observable<AuthResponse> {
    const headers = this.getAuthHeaders();

    return this.http
      .post<CacLoginResponse>(`${this.cacApiUrl}/auth/refresh`, {}, { headers })
      .pipe(
        tap(response => {
          if (response.access_token) {
            localStorage.setItem(this.tokenKey, response.access_token);
          }
        }),
        switchMap(response =>
          this.fetchCacProfile().pipe(
            map(user => ({ access_token: response.access_token, token_type: response.token_type, user } as AuthResponse))
          )
        ),
        catchError(error => {
          this.clearAuthState();
          return throwError(() => error);
        })
      );
  }

  /**
   * Récupère le profil complet du user connecté (depuis CAC)
   */
  getCurrentUserProfile(): Observable<UserInfo> {
    return this.fetchCacProfile();
  }

  /**
   * Changement de mot de passe via le backend CAC
   */
  changePassword(request: ChangePasswordRequest): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http
      .post(`${this.cacApiUrl}/user/me/change-password`, {
        current_password: request.currentPassword,
        new_password: request.newPassword
      }, { headers })
      .pipe(
        catchError(error => throwError(() => error))
      );
  }

  /**
   * Enregistrement d'un nouvel utilisateur (admin CAC)
   */
  register(request: CreateUserRequest): Observable<UserInfo> {
    return this.http
      .post<CacUserProfile>(`${this.cacApiUrl}/auth/register`, request)
      .pipe(
        map(profile => this.mapCacProfile(profile)),
        catchError(error => throwError(() => error))
      );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value && !!this.getStoredToken();
  }

  /**
   * Get current user
   */
  getCurrentUser(): UserInfo | null {
    const user = this.currentUserSubject.value;
    console.log('[Auth Debug] getCurrentUser() called, returning:', user);
    console.log('[Auth Debug] localStorage token:', !!this.getStoredToken());
    console.log('[Auth Debug] localStorage user:', !!this.getStoredUser());
    console.log('[Auth Debug] Session expired:', this.isSessionExpired());
    return user;
  }

  /**
   * Check if user has admin role
   */
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'ADMIN';
  }

  /**
   * Check if user has commercial role
   */
  isCommercial(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'COMMERCIAL';
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return this.getStoredToken();
  }

  loginWithHubSpot(): void {
    console.warn('HubSpot login non disponible avec le backend CAC.');
  }

  isHubSpotUser(): boolean {
    return false;
  }

  private clearAuthState(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.sessionExpiryKey);
    localStorage.removeItem(this.lastActivityKey);
    localStorage.removeItem('redirectUrl');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('nom');
    localStorage.removeItem('prenom');
    localStorage.removeItem('username');

    this.clearSessionTimer();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Get stored authentication token
   */
  private getStoredToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Store user information
   */
  private storeUser(user: UserInfo): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
    this.syncSharedUserStorage(user);
  }

  /**
   * Même session que le CAS (current_user), mais la sidebar / certains écrans
   * utilisent encore le AuthService partagé qui lit role / email dans localStorage.
   */
  private syncSharedUserStorage(user: UserInfo): void {
    if (user.role) {
      localStorage.setItem('role', user.role);
    }
    if (user.email) {
      localStorage.setItem('email', user.email);
    }
    if (user.nom) {
      localStorage.setItem('nom', user.nom);
    }
    if (user.prenom) {
      localStorage.setItem('prenom', user.prenom);
    }
    if (user.telephone) {
      localStorage.setItem('telephone', user.telephone);
    }
    if (user.telephone_fixe) {
      localStorage.setItem('telephone_fixe', user.telephone_fixe);
    }
    if (user.photo_url) {
      localStorage.setItem('photo_url', user.photo_url);
    }
    const display = user.prenom || user.username || user.email || '';
    if (display) {
      localStorage.setItem('username', display);
    }
  }

  /**
   * Get stored user information
   */
  private getStoredUser(): UserInfo | null {
    const stored = localStorage.getItem(this.userKey);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Get authorization headers
   */
  public getAuthHeaders(): HttpHeaders {
    const token = this.getStoredToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Une erreur s\'est produite';

    if (error.status === 403) {
      // Account status errors
      const detail = error.error?.detail || error.error?.message || '';
      if (detail.includes('inactif') || detail.includes('suspendu') || detail.includes('HubSpot')) {
        return throwError(() => error);
      }
      errorMessage = 'Accès refusé';
    } else if (error.status === 401) {
      errorMessage = 'Email ou mot de passe incorrect';
    } else if (error.status === 422) {
      errorMessage = 'Données de connexion invalides';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de se connecter au serveur';
    } else if (error.error?.detail || error.error?.message) {
      errorMessage = error.error.detail || error.error.message;
    }

    console.error('❌ Auth error:', errorMessage);
    return throwError(() => error);
  }

  /**
   * Initialiser la session avec timestamp d'expiration
   */
  private initializeSession(): void {
    const now = Date.now();
    const expiryTime = now + this.SESSION_DURATION;

    localStorage.setItem(this.sessionExpiryKey, expiryTime.toString());
    localStorage.setItem(this.lastActivityKey, now.toString());

    this.startSessionTimer();
    console.log('✅ Session initialisée, expiration dans 2 heures');
  }

  /**
   * Vérifier si la session a expiré
   */
  private isSessionExpired(): boolean {
    const expiryTime = localStorage.getItem(this.sessionExpiryKey);
    if (!expiryTime) {
      console.log('🔍 Pas de timestamp d\'expiration trouvé');
      return true;
    }

    const now = Date.now();
    const expiry = parseInt(expiryTime);
    const timeLeft = expiry - now;

    console.log('🔍 Vérification expiration session:', {
      now: new Date(now).toLocaleString(),
      expiry: new Date(expiry).toLocaleString(),
      timeLeftMinutes: Math.round(timeLeft / (1000 * 60)),
      isExpired: now > expiry
    });

    return now > expiry;
  }

  /**
   * Mettre à jour la dernière activité et prolonger la session
   */
  private updateLastActivity(): void {
    const now = Date.now();
    const newExpiryTime = now + this.SESSION_DURATION;

    localStorage.setItem(this.lastActivityKey, now.toString());
    localStorage.setItem(this.sessionExpiryKey, newExpiryTime.toString());

    // Redémarrer le timer
    this.startSessionTimer();
  }

  /**
   * Démarrer le timer de session
   */
  private startSessionTimer(): void {
    this.clearSessionTimer();

    const expiryTime = localStorage.getItem(this.sessionExpiryKey);
    if (!expiryTime) return;

    const timeUntilExpiry = parseInt(expiryTime) - Date.now();

    if (timeUntilExpiry > 0) {
      this.sessionTimer = setTimeout(() => {
        console.log('⏰ Session expirée automatiquement après 2 heures');
        this.logout().subscribe();
      }, timeUntilExpiry);
    }
  }

  /**
   * Nettoyer le timer de session
   */
  private clearSessionTimer(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  /**
   * Configurer le suivi d'activité utilisateur
   */
  private setupActivityTracking(): void {
    // Throttle pour éviter trop d'appels
    let lastUpdate = 0;
    const throttleDelay = 30000; // 30 secondes

    // Écouter les événements d'activité utilisateur
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, () => {
        const now = Date.now();
        if (this.isAuthenticated() && (now - lastUpdate > throttleDelay)) {
          this.updateLastActivity();
          lastUpdate = now;
        }
      }, { passive: true });
    });

    // Vérifier périodiquement l'expiration de session
    setInterval(() => {
      if (this.isAuthenticated() && this.isSessionExpired()) {
        console.log('⏰ Session expirée, déconnexion automatique');
        this.logout().subscribe();
      }
    }, 60000); // Vérifier chaque minute
  }
}